package me.repeater64.advancedmpkeditor.gui.screens.barrel.random_slot

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.FlowRowOverflow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Info
import androidx.compose.material3.Checkbox
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.PlainTooltip
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TooltipAnchorPosition
import androidx.compose.material3.TooltipBox
import androidx.compose.material3.TooltipDefaults
import androidx.compose.material3.rememberTooltipState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.toMutableStateList
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import me.repeater64.advancedmpkeditor.backend.data_object.CopyPasteable
import me.repeater64.advancedmpkeditor.backend.data_object.item.DontReplaceMinecraftItem
import me.repeater64.advancedmpkeditor.backend.data_object.random_slot.RandomSlotOptionsSet
import me.repeater64.advancedmpkeditor.backend.data_object.random_slot.RandomSlotsData
import me.repeater64.advancedmpkeditor.backend.data_object.randomiser.WeightedOption
import me.repeater64.advancedmpkeditor.backend.data_object.randomiser.WeightedOptionList
import me.repeater64.advancedmpkeditor.gui.component.DragSwappable
import me.repeater64.advancedmpkeditor.gui.component.MinecraftSlotDisplay
import me.repeater64.advancedmpkeditor.gui.component.MinecraftSlotDisplayMulti
import me.repeater64.advancedmpkeditor.gui.component.MinecraftSlotDisplayMultiImpl

@OptIn(ExperimentalMaterial3Api::class, ExperimentalFoundationApi::class)
@Composable
fun ColumnScope.RandomSlotsEditor(
    randomSlotsData: RandomSlotsData,
    allLabels: MutableSet<String>,
    showDialogCallback: (@Composable () -> Any) -> Any,
    hideDialogCallback: () -> Unit,
    getClipboardCallback: () -> CopyPasteable<*>?,
    setClipboardCallback: (CopyPasteable<*>?) -> Unit,
) {
    Text("Random Slot Items", style = MaterialTheme.typography.headlineLarge)
    Spacer(Modifier.height(15.dp))

    val tooltipState0 = rememberTooltipState()
    TooltipBox(
        positionProvider = TooltipDefaults.rememberTooltipPositionProvider(TooltipAnchorPosition.Above),
        tooltip = {
            PlainTooltip(modifier=Modifier.width(350.dp)) { Text(text = "If enabled, the number of crafted beds+anchors that you have will be announced in chat at the start. This also includes TNT, so for fortress practice you can use it to see if you have a single/double blaze bed or TNT.") }
        },
        state = tooltipState0,
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Checkbox(
                checked = randomSlotsData.announceExplosives,
                onCheckedChange = { randomSlotsData.announceExplosives = it },
                modifier = Modifier.size(20.dp),
            )
            Spacer(Modifier.width(6.dp))
            Text("Announce Explosives Count", style = MaterialTheme.typography.titleMedium)
            Spacer(Modifier.width(4.dp))
            Icon(
                imageVector = Icons.Default.Info,
                contentDescription = "Hover for Info",
                tint = MaterialTheme.colorScheme.primary,
                modifier = Modifier.size(20.dp)
            )
        }
    }

    Spacer(Modifier.height(15.dp))

    val tooltipStateBow = rememberTooltipState()
    TooltipBox(
        positionProvider = TooltipDefaults.rememberTooltipPositionProvider(TooltipAnchorPosition.Above),
        tooltip = {
            PlainTooltip(modifier=Modifier.width(350.dp)) { Text(text = "If enabled, you will get a message in chat at the start telling you if you have a bow/crossbow/arrows or not.") }
        },
        state = tooltipStateBow,
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Checkbox(
                checked = randomSlotsData.announceBow,
                onCheckedChange = { randomSlotsData.announceBow = it },
                modifier = Modifier.size(20.dp),
            )
            Spacer(Modifier.width(6.dp))
            Text("Announce Bow/Crossbow/Arrows", style = MaterialTheme.typography.titleMedium)
            Spacer(Modifier.width(4.dp))
            Icon(
                imageVector = Icons.Default.Info,
                contentDescription = "Hover for Info",
                tint = MaterialTheme.colorScheme.primary,
                modifier = Modifier.size(20.dp)
            )
        }
    }

    Spacer(Modifier.height(15.dp))

    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        for ((index, optionSet) in randomSlotsData.optionsSets.withIndex()) {
            RandomSlotsRow(randomSlotsData, index+37, optionSet, allLabels, showDialogCallback, hideDialogCallback, getClipboardCallback, setClipboardCallback)
        }

        // Add new
        val tooltipState = rememberTooltipState()
        TooltipBox(
            positionProvider = TooltipDefaults.rememberTooltipPositionProvider(TooltipAnchorPosition.Above),
            tooltip = {
                PlainTooltip() { Text(text = "Click to add!") }
            },
            state = tooltipState,
        ) {
            DragSwappable(key = -1,
                accountForOffset = false,
                ghostContent = {}) { _, isHovered ->
                Surface(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(120.dp)
                        .padding(horizontal = 50.dp, vertical = 8.dp),
                    border = if (isHovered) BorderStroke(1.dp, MaterialTheme.colorScheme.outline) else null,
                    shape = RoundedCornerShape(16.dp),
                    color = MaterialTheme.colorScheme.surface,
                    tonalElevation = (-30).dp,
                    shadowElevation = 4.dp,
                    onClick = {
                        val newOptionsSet = RandomSlotOptionsSet("Unnamed", WeightedOptionList(mutableListOf(WeightedOption(listOf(DontReplaceMinecraftItem(true)).toMutableStateList()))))
                        randomSlotsData.optionsSets.add(newOptionsSet)
                        showDialogCallback { RandomSlotPopup(newOptionsSet, allLabels, hideDialogCallback, deleteCallback = {randomSlotsData.optionsSets.remove(newOptionsSet)}, getClipboardCallback, setClipboardCallback) }
                    },
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.Center) {
                        Icon(
                            imageVector = Icons.Default.Add,
                            contentDescription = "Add",
                            tint = MaterialTheme.colorScheme.primary,
                            modifier = Modifier.size(60.dp)
                        )
                    }
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class, ExperimentalFoundationApi::class)
@Composable
fun ColumnScope.RandomSlotsRow(
    data: RandomSlotsData,
    dragSwappableKey: Int,
    optionsSet: RandomSlotOptionsSet,
    allLabels: MutableSet<String>,
    showDialogCallback: (@Composable () -> Any) -> Any,
    hideDialogCallback: () -> Unit,
    getClipboardCallback: () -> CopyPasteable<*>?,
    setClipboardCallback: (CopyPasteable<*>?) -> Unit,
) {
    val tooltipState = rememberTooltipState()
    TooltipBox(
        positionProvider = TooltipDefaults.rememberTooltipPositionProvider(TooltipAnchorPosition.Above),
        tooltip = {
            PlainTooltip() { Text(text = if (optionsSet.canAlwaysFitInOneSlot()) "Click to edit!\n\nDrag to reorder, or swap with an inventory/hotbar slot!" else "Click to edit!\n\nDrag to reorder!") }
        },
        state = tooltipState,
    ) {
        DragSwappable(key = dragSwappableKey,
            accountForOffset = false,
            ghostContent = {
                MinecraftSlotDisplayMultiImpl(optionsSet.options.options.flatMap { it.option }, 75).ContentsOnly()
            }) { _, isHovered ->
            Surface(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(120.dp)
                    .padding(horizontal = 50.dp, vertical = 8.dp),
                border = if (isHovered) BorderStroke(1.dp, MaterialTheme.colorScheme.outline) else null,
                shape = RoundedCornerShape(16.dp),
                color = MaterialTheme.colorScheme.surface,
                tonalElevation = (-30).dp,
                shadowElevation = 4.dp,
                onClick = {
                    showDialogCallback {
                        RandomSlotPopup(
                            optionsSet,
                            allLabels,
                            hideDialogCallback,
                            deleteCallback = { data.optionsSets.remove(optionsSet) },
                            getClipboardCallback = getClipboardCallback,
                            setClipboardCallback = setClipboardCallback)
                    }
                },
            ) {
                Column(horizontalAlignment = Alignment.Start) {
                    Text(
                        optionsSet.setName,
                        style = MaterialTheme.typography.headlineSmall,
                        modifier = Modifier.padding(start = 20.dp, top = 5.dp)
                    )


                    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
                        val availableWidth = maxWidth.value


                        Row(verticalAlignment = Alignment.CenterVertically) {
                            var widthSoFar = 0.0f
                            for ((index, option) in optionsSet.options.options.withIndex()) {
                                widthSoFar += 50 // For padding + or text
                                widthSoFar += 50 * (option.option.size) // For items

                                if (widthSoFar > availableWidth - 20) {
                                    Text(" ...", style = MaterialTheme.typography.bodyLarge)
                                    return@Row
                                } else {
                                    Surface(
                                        modifier = Modifier
                                            .padding(horizontal = 15.dp),
                                        shape = RoundedCornerShape(4.dp),
                                        color = MaterialTheme.colorScheme.surface,
                                        tonalElevation = 20.dp,
                                        shadowElevation = 6.dp,
                                    ) {
                                        Row(verticalAlignment = Alignment.CenterVertically) {
                                            for (item in option.option) {
                                                MinecraftSlotDisplay(item, 50).ContentsOnly()
                                            }
                                        }
                                    }
                                }
                                if (index != optionsSet.options.options.size - 1) {
                                    Text("or", style = MaterialTheme.typography.bodyLarge)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}